import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../utils/axiosConfig';
import { dashboardService } from '../services/dashboardService';
import DeliveryDocumentUploadModal from './DeliveryDocumentUploadModal';
import { DeliveryDocumentGroup } from './DeliveryDocumentComponents';
import TradeOpsDeleteModal from './TradeOpsDeleteModal';
import { CloseIconSmall } from './ChecklistIcons';
import { getErrorMessage } from '../utils/errorMessages';
import { confirmName, lookupNames } from '../services/documentNamingService';
import SuggestedName from './DocumentNaming/SuggestedName';
import useSuggestionPolling from './DocumentNaming/useSuggestionPolling';
import ContractsInContextPanel from './Legal/ContractsInContextPanel';
import PdfPreviewModal from './shared/PdfPreviewModal';
import { useDocumentViewer } from './DocumentsSplitPane';
import {
    DELIVERY_DOCUMENT_CATEGORIES,
    filterCategoriesForCommodity,
} from '../constants/documentTypeCategories';
import './DocumentPanel.css';


const extractDocumentInfo = (document) => {
    const fields = document.Fields || [];
    const getFieldValue = (fieldName) => {
        const field = fields.find(f => f.FieldName === fieldName);
        return field?.Item || null;
    };
    return {
        documentId: document.Id,
        filename: getFieldValue('SUBJECT') || 'Unknown Document',
        deliveryId: getFieldValue('DELIVERY_ID'),
        employeeId: getFieldValue('EMPLOYEE_ID'),
        dateUploaded: getFieldValue('DATE_UPLOADED'),
        documentType: getFieldValue('DELIVERY_DOCUMENT_TYPE'),
        fileSize: document.TotalPages || 0
    };
};

// ``docked`` switches the panel from a COVER to a COLUMN.
//
// Overlay mode (the default, unchanged) is a fixed full-screen dark drawer:
// the delivery behind it is dimmed, blurred and unscrollable, so paperwork and
// delivery can never be read together -- the whole point of Marcel's request.
// Docked mode drops the overlay and lets the host screen place this panel in a
// grid column beside the delivery. Kept as a mode rather than a rewrite so the
// overlay presentation is still there for any host that wants it.
const DocumentPanel = ({ isOpen, onClose, deliveryId, commodityType, buyerPartnerName = '', sellerPartnerName = '', showSuccess, showError, onDocumentCountChange, docked = false }) => {
    // Sprint 4A: filter the static DELIVERY_DOCUMENT_CATEGORIES constant
    // down to the categories visible for this commodity. A category with
    // no ``commodityType`` key is universal (Solids + Fuels both see it);
    // a category with ``commodityType: 'fuels'`` is included only when
    // the delivery is Fuels. Single gating point so the file-explorer
    // grouping, the upload modal's category buttons, and the
    // ``allDocumentTypes`` lookup all stay consistent.
    const effectiveCategories = useMemo(
        () => filterCategoriesForCommodity(DELIVERY_DOCUMENT_CATEGORIES, commodityType),
        [commodityType]
    );
    const allDocumentTypes = useMemo(
        () => effectiveCategories.flatMap(cat => cat.types),
        [effectiveCategories]
    );

    const [documents, setDocuments] = useState([]);
    // "We could not ask" is a different answer from "there is nothing here",
    // and the panel has to be able to tell them apart. Without this the list
    // fetch failing -- the server refusing this reader, DocuWare down, the
    // token expired -- left the list empty and the panel said "No Documents
    // Found. Start building your delivery documentation." A refusal presented
    // as an empty shelf is the worst of both: the reader believes the delivery
    // has no paperwork, and nobody ever reports a bug.
    const [loadError, setLoadError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateFileInfo, setDuplicateFileInfo] = useState(null);
    
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    
    // Non-null only when this panel is rendered inside a DocumentsSplitPane.
    const viewer = useDocumentViewer();
    const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
    const [previewFileName, setPreviewFileName] = useState('');

    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    const [employeeInfoCache, setEmployeeInfoCache] = useState({});
    const [displayNamesByDocId, setDisplayNamesByDocId] = useState({});
    useSuggestionPolling(displayNamesByDocId, setDisplayNamesByDocId, 'delivery');
    
    const panelRef = useRef(null);

    // Pick whichever partner name is available; buyer wins if both are set,
    // matching the typical "documents go to the buyer" trade flow. The
    // suggestion form lets the user override either way.
    const effectivePartnerName = buyerPartnerName || sellerPartnerName || '';

    useEffect(() => {
        if (deliveryId && deliveryId !== 'new' && deliveryId !== 'undefined' && isOpen) {
            fetchDocuments();
        }
        if (isOpen) {
            setSearchQuery('');
        }
    }, [deliveryId, isOpen]);

    // Update document count whenever documents change
    useEffect(() => {
        if (onDocumentCountChange) {
            onDocumentCountChange(documents.length);
        }
    }, [documents, onDocumentCountChange]);

    // Handle clicks outside the panel to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Docked mode has no outside. The panel sits BESIDE the delivery,
            // so "clicked outside the panel" is just "clicked the delivery" --
            // closing on that would snap the paperwork shut the moment anyone
            // touched the record they were checking it against.
            if (docked) {
                return;
            }

            // Don't close if any modal is open
            if (showUploadModal || showDuplicateModal || showDeleteConfirmation) {
                return;
            }
            
            if (panelRef.current && !panelRef.current.contains(event.target) && isOpen) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, docked, showUploadModal, showDuplicateModal, showDeleteConfirmation]);

    // Handle escape key to close panel
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Get current user information
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfoData = await dashboardService.getUserInfo();
                setCurrentUserInfo(userInfoData);
            } catch (error) {
                console.error('Error fetching user info:', error);
            }
        };

        if (!currentUserInfo) {
            fetchUserInfo();
        }
    }, [currentUserInfo]);

    const filteredCategoryGroups = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();

        return effectiveCategories.map(category => {
            const categoryDocs = documents.filter(doc => {
                const fields = doc.Fields || [];
                const docTypeField = fields.find(f => f.FieldName === 'DELIVERY_DOCUMENT_TYPE');
                const docType = docTypeField?.Item || '';
                return category.types.includes(docType);
            });

            if (!query) return { ...category, documents: categoryDocs };

            const filtered = categoryDocs.filter(doc => {
                const info = extractDocumentInfo(doc);
                const haystack = [
                    info.filename,
                    info.documentType || '',
                    category.label
                ].join(' ').toLowerCase();
                return haystack.includes(query);
            });

            return { ...category, documents: filtered };
        });
    }, [documents, searchQuery, effectiveCategories]);

    const uncategorizedDocs = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        const uncategorized = documents.filter(doc => {
            const fields = doc.Fields || [];
            const docTypeField = fields.find(f => f.FieldName === 'DELIVERY_DOCUMENT_TYPE');
            const docType = docTypeField?.Item || '';
            return !allDocumentTypes.includes(docType);
        });
        if (!query) return uncategorized;
        return uncategorized.filter(doc => {
            const info = extractDocumentInfo(doc);
            return [info.filename, info.documentType || ''].join(' ').toLowerCase().includes(query);
        });
    }, [documents, searchQuery, allDocumentTypes]);

    const fetchDocuments = async () => {
        if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') return;
        
        setIsLoading(true);
        setLoadError(null);

        try {
            const response = await axiosInstance.get('/api/docuware/delivery-documents/documents/', {
                params: {
                    delivery_id: deliveryId,
                    count: 100
                }
            });

            const fetchedDocuments = response.data.Items || [];
            setDocuments(fetchedDocuments);

        } catch (error) {
            console.error('Error fetching delivery documents:', error);
            // Remember the failure as well as announcing it. The toast is gone
            // in five seconds; the list stays on screen, and it must not go on
            // claiming this delivery has no documents.
            setDocuments([]);
            setLoadError(
                error?.response?.status === 403
                    ? 'You do not have access to this delivery’s documents.'
                    : 'We could not load this delivery’s documents. This is not the same as there being none — try Refresh.'
            );
            showError('Failed to load delivery documents');
        } finally {
            setIsLoading(false);
        }
    };

    // Bulk-fetch the AI display names for the current document list. The
    // lookup endpoint returns nothing for legacy docs that pre-date this
    // feature, so the panel falls back to DocuWare SUBJECT in that case.
    const fetchDisplayNames = useCallback(async (docs) => {
        const ids = (docs || [])
            .map(d => d?.Id)
            .filter(Boolean)
            .map(String);
        if (ids.length === 0) {
            setDisplayNamesByDocId({});
            return;
        }
        try {
            const map = await lookupNames(ids, 'delivery');
            setDisplayNamesByDocId(map || {});
        } catch (err) {
            console.warn('Failed to fetch AI display names:', err);
            setDisplayNamesByDocId({});
        }
    }, []);

    useEffect(() => {
        fetchDisplayNames(documents);
    }, [documents, fetchDisplayNames]);

    // Wrap the raw DocuWare extractor to layer in the AI display name
    // when one exists. List rows prefer ``displayName`` over the legacy
    // ``filename`` so the UI renders the human-friendly composed name
    // (date - type - number - partner - location) instead of the raw
    // upload filename.
    const getDocumentInfo = (document) => {
        const base = extractDocumentInfo(document);
        const docId = document?.Id != null ? String(document.Id) : '';
        const aiRow = docId ? displayNamesByDocId[docId] : null;
        return {
            ...base,
            aiDisplayName: aiRow?.display_name || '',
            displayName: aiRow?.display_name || base.filename,
            nameRow: aiRow || null,
            onNameRow: (updated) => setDisplayNamesByDocId((prev) => ({ ...(prev || {}), [docId]: updated })),
        };
    };

    // Duplicate detection helper functions
    const checkForDuplicateFilename = (filename) => {
        console.log('🔍 FRONTEND: Checking for duplicate delivery filename:', filename);
        console.log('🔍 FRONTEND: Current delivery documents:', documents);
        
        if (!documents || documents.length === 0) {
            console.log('✅ FRONTEND: No existing delivery documents, no duplicates found');
            return { isDuplicate: false, existingDocument: null };
        }
        
        // Check if any existing document has the same filename in SUBJECT field
        const duplicateDocument = documents.find(doc => {
            const docInfo = getDocumentInfo(doc);
            const isDup = docInfo.filename === filename;
            console.log(`🔍 FRONTEND: Comparing "${docInfo.filename}" with "${filename}": ${isDup}`);
            return isDup;
        });
        
        if (duplicateDocument) {
            console.log('⚠️ FRONTEND: Delivery duplicate found:', duplicateDocument);
            return { isDuplicate: true, existingDocument: duplicateDocument };
        }
        
        console.log('✅ FRONTEND: No delivery duplicates found');
        return { isDuplicate: false, existingDocument: null };
    };

    const generateIncrementedFilename = (originalFilename) => {
        console.log('🔢 FRONTEND: Generating incremented delivery filename for:', originalFilename);
        
        if (!documents || documents.length === 0) {
            console.log('✅ FRONTEND: No existing delivery documents, returning original filename');
            return originalFilename;
        }
        
        // Extract existing filenames from SUBJECT fields
        const existingFilenames = documents.map(doc => {
            const docInfo = getDocumentInfo(doc);
            return docInfo.filename;
        }).filter(Boolean); // Remove any null/undefined values
        
        console.log('📋 FRONTEND: Existing delivery filenames:', existingFilenames);
        
        // Parse the original filename to extract name and extension
        const lastDotIndex = originalFilename.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
        const extension = lastDotIndex !== -1 ? originalFilename.substring(lastDotIndex) : '';
        
        console.log('📝 FRONTEND: Name without extension:', nameWithoutExt);
        console.log('📝 FRONTEND: Extension:', extension);
        
        // Start checking from (1) and increment until we find a unique name
        let counter = 1;
        let newFilename = `${nameWithoutExt} (${counter})${extension}`;
        
        while (existingFilenames.includes(newFilename)) {
            counter++;
            newFilename = `${nameWithoutExt} (${counter})${extension}`;
            console.log(`🔢 FRONTEND: Trying counter ${counter}: ${newFilename}`);
        }
        
        console.log(`✅ FRONTEND: Final incremented delivery filename: ${newFilename}`);
        return newFilename;
    };

    // File upload handler
    const handleFileUpload = async (documentType, file, namingPayload = null) => {
        // Validation
        if (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') {
            showError('Please save the delivery before uploading documents.');
            return;
        }
        
        if (!documentType) {
            showError('Please select a document type.');
            return;
        }
        
        // File size validation (30MB)
        const maxSizeInBytes = 30 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            showError(`File size exceeds 30MB limit.`);
            return;
        }
        
        console.log('📤 FRONTEND: Starting delivery file upload process');
        console.log('📄 FRONTEND: File details:', { name: file.name, size: file.size, type: file.type });
        
        // Check for duplicate filename before proceeding with upload
        const duplicateCheck = checkForDuplicateFilename(file.name);
        
        if (duplicateCheck.isDuplicate) {
            console.log('⚠️ FRONTEND: Delivery duplicate detected, showing modal');
            // Store the upload details for later processing
            setDuplicateFileInfo({
                documentType,
                file,
                existingDocument: duplicateCheck.existingDocument,
                namingPayload,
            });
            setShowDuplicateModal(true);
            return; // Stop here and wait for user decision
        }
        
        // No duplicate found, proceed with normal upload
        console.log('✅ FRONTEND: No delivery duplicate found, proceeding with upload');
        await performFileUpload(documentType, file, file.name, namingPayload);
    };

    // Persist the AI-suggested display name to the document_naming app
    // after the DocuWare upload has returned a stable document ID.
    // Failure here is logged but never blocks the user — the document
    // is already in DocuWare and the panel falls back to the SUBJECT
    // field if the row is missing.
    const persistDisplayName = async (documentId, file, documentType, namingPayload) => {
        if (!documentId || !namingPayload) return;
        try {
            await confirmName({
                docuwareDocumentId: documentId,
                module: 'delivery',
                file,
                typeDropdownValue: namingPayload.typeDropdownValue || documentType || '',
                partnerName: namingPayload.partnerName || '',
                relatedEntityId: deliveryId,
                originalFilename: file?.name || '',
                components: namingPayload.components || {},
                aiSuggestion: namingPayload.aiSuggestion || {},
                userOverrides: namingPayload.userOverrides || [],
                extractionConfidence: namingPayload.extractionConfidence ?? null,
                isUserConfirmed: true,
            });
        } catch (err) {
            console.warn('AI display name persist failed (non-blocking):', err);
        }
    };

    // Separate function to handle the actual upload process
    const performFileUpload = async (documentType, file, finalFilename, namingPayload = null) => {
        console.log('🚀 FRONTEND: Performing delivery file upload with filename:', finalFilename);
        
        setUploading(true);
        
        try {
            const employeeId = currentUserInfo?.user_id || '1';
            const subject = finalFilename; // Use the final filename (potentially renamed) as subject
            
            console.log('📋 FRONTEND: Delivery Upload metadata:', {
                employeeId,
                subject,
                documentType,
                deliveryId
            });
            
            // Step 1: Create document record
            const recordData = {
                delivery_id: deliveryId.toString(),
                employee_id: employeeId,
                subject: subject,
                delivery_document_type: documentType
            };
            
            console.log('📝 FRONTEND: Creating delivery document record...');
            const recordResponse = await axiosInstance.post('/api/docuware/delivery-documents/create-record/', recordData);
            
            if (!recordResponse.data.success || !recordResponse.data.document_id) {
                throw new Error('Failed to create document record');
            }
            
            const documentId = recordResponse.data.document_id;
            console.log('✅ FRONTEND: Delivery Document record created with ID:', documentId);
            
            // Step 2: Upload file
            console.log('📤 FRONTEND: Uploading delivery file content...');
            const fileFormData = new FormData();
            fileFormData.append('file', file);
            
            await axiosInstance.post(`/api/docuware/delivery-documents/upload-file/${documentId}/`, fileFormData);
            
            // Step 3: Persist the AI display name (non-blocking)
            await persistDisplayName(documentId, file, documentType, namingPayload);
            
            console.log('🎉 FRONTEND: Delivery File upload completed successfully');
            showSuccess(`Document "${finalFilename}" uploaded successfully for ${documentType}!`);
            
            // Refresh document list
            setTimeout(() => {
                fetchDocuments();
            }, 1000);
            
        } catch (error) {
            console.error('❌ FRONTEND: Delivery upload error:', error);
            showError(getErrorMessage(error, 'Document upload failed. Please try again.'));
        } finally {
            setUploading(false);
        }
    };

    // Duplicate modal handlers
    const handleDuplicateKeepBoth = async () => {
        console.log('👥 FRONTEND: User chose to keep both delivery files');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate delivery file info available');
            return;
        }
        
        const { documentType, file, namingPayload } = duplicateFileInfo;
        const incrementedFilename = generateIncrementedFilename(file.name);
        
        console.log('📝 FRONTEND: Renaming delivery file to:', incrementedFilename);
        
        // Close modal and proceed with upload using incremented filename
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
        
        await performFileUpload(documentType, file, incrementedFilename, namingPayload);
    };

    const handleDuplicateReplace = async () => {
        console.log('🔄 FRONTEND: User chose to replace existing delivery file');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate delivery file info available');
            return;
        }
        
        const { documentType, file, existingDocument, namingPayload } = duplicateFileInfo;
        
        try {
            console.log('🗑️ FRONTEND: Deleting existing delivery document:', existingDocument.Id);
            
            // Delete the existing document first
            await axiosInstance.delete(`/api/docuware/delivery-documents/delete/${existingDocument.Id}/`);
            
            console.log('✅ FRONTEND: Existing delivery document deleted successfully');
            
            // Close modal and proceed with upload using original filename
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
            
            await performFileUpload(documentType, file, file.name, namingPayload);
            
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting existing delivery document:', error);
            showError(getErrorMessage(error, 'Failed to delete existing document. Please try again.'));
            
            // Close modal even if deletion failed
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
        }
    };

    const handleDuplicateCancel = () => {
        console.log('❌ FRONTEND: User cancelled delivery upload due to duplicate');
        
        // Simply close the modal and reset state
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
    };

    // Get employee information by Django user ID
    const getEmployeeInfoByUserId = async (djangoUserId) => {
        if (!djangoUserId) return null;
        
        // Check cache first
        if (employeeInfoCache[djangoUserId]) {
            return employeeInfoCache[djangoUserId];
        }
        
        try {
            // For current user, use their own info
            if (currentUserInfo && currentUserInfo.user_id === parseInt(djangoUserId)) {
                const employeeInfo = {
                    first_name: currentUserInfo.first_name,
                    last_name: currentUserInfo.last_name,
                    position: currentUserInfo.position,
                    department: currentUserInfo.department,
                    office: currentUserInfo.office,
                    employee_id: currentUserInfo.employee_id
                };
                
                // Cache the result
                setEmployeeInfoCache(prev => ({
                    ...prev,
                    [djangoUserId]: employeeInfo
                }));
                
                return employeeInfo;
            } else {
                // For other users, return placeholder
                const placeholderInfo = {
                    first_name: 'User',
                    last_name: `ID ${djangoUserId}`,
                    position: 'Unknown',
                    department: 'Unknown',
                    office: 'Unknown',
                    employee_id: djangoUserId
                };
                
                return placeholderInfo;
            }
        } catch (error) {
            console.error(`Error fetching employee info for user ID ${djangoUserId}:`, error);
            return null;
        }
    };

    // Document download handler
    const downloadDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(`/api/docuware/delivery-documents/download/${documentId}/`, {
                responseType: 'blob'
            });
            
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            showSuccess(`Document "${filename}" downloaded successfully`);
        } catch (error) {
            console.error('Error downloading document:', error);
            showError(`Failed to download "${filename}"`);
        }
    };

    // Open the document in the app instead of saving it to disk.
    //
    // Deliberately the SAME request the download button already makes -- same
    // URL, same permission gate, same provenance binding -- just held in
    // memory and handed to the viewer rather than written to the Downloads
    // folder. It opens no new access surface: a user who is refused the
    // download is refused the preview by exactly the same rule.
    const previewDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(`/api/docuware/delivery-documents/download/${documentId}/`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const name = filename || 'document.pdf';
            setPreviewPdfUrl(url);
            setPreviewFileName(name);
            // Inside the split pane, the bunker note is read BESIDE the
            // delivery -- which is what Marcel asked for and what a pop-up
            // over the top of it cannot do. Outside a pane there is nothing
            // to read it beside, so the pop-up stays.
            if (viewer) viewer.open(url, name);
        } catch (error) {
            console.error('Error previewing document:', error);
            showError(`Failed to open "${filename}"`);
        }
    };

    const closePreview = () => {
        if (previewPdfUrl) {
            window.URL.revokeObjectURL(previewPdfUrl);
        }
        setPreviewPdfUrl(null);
        setPreviewFileName('');
    };

    // Document delete handler
    const deleteDocument = async (documentId, filename) => {
        try {
            await axiosInstance.delete(`/api/docuware/delivery-documents/delete/${documentId}/`);
            
            showSuccess(`Document "${filename}" deleted successfully`);
            
            // Refresh document list
            setTimeout(() => {
                fetchDocuments();
            }, 500);
            
        } catch (error) {
            console.error('Error deleting document:', error);
            showError(`Failed to delete "${filename}"`);
        }
    };

    // Utility functions
    const formatDate = (dateString) => {
        console.log('formatDate: Input received:', {
            value: dateString,
            type: typeof dateString,
            isNull: dateString === null,
            isUndefined: dateString === undefined,
            isEmpty: dateString === '',
            stringValue: String(dateString)
        });
        
        if (!dateString) {
            console.log('formatDate: No date string provided, returning "Unknown"');
            return 'Unknown';
        }
        
        try {
            let date;
            let cleanDateString;
            
            if (typeof dateString === 'string') {
                // Check if it's a .NET JSON date format: /Date(1751569475249)/
                const netDateMatch = dateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    console.log('formatDate: .NET JSON date detected:', {
                        original: dateString,
                        extractedTimestamp: timestamp,
                        timestampType: typeof timestamp
                    });
                    date = new Date(timestamp);
                } else {
                    // Handle regular ISO format dates (with Z suffix)
                    cleanDateString = dateString.replace(/Z+$/, 'Z').trim();
                    console.log('formatDate: ISO date format, cleaned:', {
                        original: dateString,
                        cleaned: cleanDateString
                    });
                    date = new Date(cleanDateString);
                }
            } else {
                console.log('formatDate: Non-string date value, attempting direct conversion');
                date = new Date(dateString);
            }
            
            console.log('formatDate: Date object created:', {
                dateObject: date,
                getTime: date.getTime(),
                isValid: !isNaN(date.getTime()),
                toISOString: isNaN(date.getTime()) ? 'INVALID' : date.toISOString()
            });
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.error('formatDate: Invalid date detected:', {
                    input: dateString,
                    cleaned: cleanDateString,
                    dateObject: date
                });
                return 'Invalid Date';
            }
            
            // Return formatted date in local format matching the compact design
            const formattedDate = date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
            
            console.log('formatDate: Successfully formatted date:', {
                input: dateString,
                output: formattedDate,
                dateObject: date
            });
            
            return formattedDate;
        } catch (error) {
            console.error('formatDate: Exception during date parsing:', {
                error: error,
                errorMessage: error.message,
                input: dateString,
                stack: error.stack
            });
            return 'Invalid Date';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };



    if (!isOpen) return null;

    // The panel's body, built once and then placed by whichever shell the mode
    // calls for. Held in a variable rather than a nested component on purpose:
    // a component declared inside a render is a NEW component type on every
    // keystroke, so React would throw the whole document list away and rebuild
    // it -- collapsing every open folder and re-running every fetch.
    const panelBody = (
        <div className="document-panel-content">
                        {/* Contracts for this delivery. The Legal module is
                            closed (Craig, 2026-08-26), so the contracts minted
                            from this delivery's recaps are read here, next to
                            the delivery's own documents. Read-only. */}
                        <ContractsInContextPanel
                            deliveryId={deliveryId}
                            title="Contracts"
                            /* This screen's own words, not the shared
                               default: on a delivery the pane covers
                               contracts AND uploads, so "nothing linked yet"
                               is the honest answer. Passed explicitly so
                               changing it here cannot move the wording on the
                               enquiry screen, which shares this panel. */
                            emptyLabel="Nothing linked yet."
                        />

                        {/* Control Buttons */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            marginBottom: '1rem',
                            padding: '0 0.5rem'
                        }}>
                            <button
                                type="button"
                                onClick={fetchDocuments}
                                disabled={isLoading}
                                style={{
                                    background: isLoading ? '#6c757d' : 'linear-gradient(135deg, #5a7b35, #4a6b25)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    opacity: isLoading ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: isLoading ? 'none' : '0 2px 4px rgba(74, 107, 37, 0.15)'
                                }}
                            >
                                {isLoading ? 'Refreshing...' : 'Refresh'}
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowUploadModal(true)}
                                disabled={uploading || !deliveryId || deliveryId === 'new' || deliveryId === 'undefined'}
                                style={{
                                    background: (uploading || !deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.5rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: (uploading || !deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? 'not-allowed' : 'pointer',
                                    opacity: (uploading || !deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: (uploading || !deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? 'none' : '0 2px 4px rgba(245, 158, 11, 0.15)'
                                }}
                            >
                                {uploading ? 'Uploading...' : 'Upload Document'}
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="doc-panel-search-wrapper">
                            <svg className="doc-panel-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                            <input
                                type="text"
                                className="doc-panel-search-input"
                                placeholder="Search documents by name or type..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="doc-panel-search-clear"
                                    onClick={() => setSearchQuery('')}
                                    type="button"
                                    aria-label="Clear search"
                                >
                                    <CloseIconSmall />
                                </button>
                            )}
                        </div>

                        {/* Document List */}
                        <div className="document-list">
                            {isLoading && (
                                <div className="doc-panel-empty-state">
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        border: '4px solid #e2e8f0',
                                        borderTop: '4px solid #4a6b25',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        marginBottom: '1rem'
                                    }}></div>
                                    <h3 className="doc-panel-empty-title">Loading Documents</h3>
                                    <p className="doc-panel-empty-text">Fetching delivery documents from DocuWare...</p>
                                </div>
                            )}

                            {/* The list could not be fetched. Say that, and say
                                it where the list would have been -- not only in
                                a toast that has already vanished. Deliberately
                                offers Refresh and NOT "upload your first
                                document": inviting an upload here tells the
                                reader the delivery is empty, which is precisely
                                what we do not know. */}
                            {!isLoading && loadError && (
                                <div className="doc-panel-empty-state" role="alert">
                                    <h3 className="doc-panel-empty-title">Documents could not be loaded</h3>
                                    <p className="doc-panel-empty-text" style={{ maxWidth: '340px' }}>
                                        {loadError}
                                    </p>
                                </div>
                            )}

                            {!isLoading && !loadError && documents.length === 0 && (
                                <div className="doc-panel-empty-state">
                                    <div style={{
                                        width: '64px',
                                        height: '64px',
                                        backgroundColor: '#eef3e6',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '1rem',
                                        border: '2px solid #4a6b25'
                                    }}>
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="#4a6b25" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14 2V8H20" stroke="#4a6b25" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M16 13H8" stroke="#4a6b25" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M16 17H8" stroke="#4a6b25" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10 9H9H8" stroke="#4a6b25" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <h3 className="doc-panel-empty-title">No Documents Found</h3>
                                    <p className="doc-panel-empty-text" style={{ maxWidth: '300px', marginBottom: '1.5rem' }}>
                                        Start building your delivery documentation by uploading your first document.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setShowUploadModal(true)}
                                        disabled={!deliveryId || deliveryId === 'new' || deliveryId === 'undefined'}
                                        style={{
                                            background: (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? '#9ca3af' : 'linear-gradient(135deg, #4a6b25, #5a7b35)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '0.75rem 1.5rem',
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s',
                                            boxShadow: (!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? 'none' : '0 2px 4px rgba(74, 107, 37, 0.15)'
                                        }}
                                    >
                                        {(!deliveryId || deliveryId === 'new' || deliveryId === 'undefined') ? 'Save Delivery First' : 'Upload First Document'}
                                    </button>
                                </div>
                            )}

                            {!isLoading && documents.length > 0 && (
                                <>
                                    {filteredCategoryGroups.map(category => (
                                        <DeliveryDocumentGroup
                                            key={category.id}
                                            documentType={category.label}
                                            categoryColor={category.color}
                                            documents={category.documents}
                                            onDownload={downloadDocument}
                                            onPreview={previewDocument}
                                            onDelete={(documentId, filename) => {
                                                const fullDocument = documents.find(doc => doc.Id === documentId);
                                                const docInfo = fullDocument ? getDocumentInfo(fullDocument) : {};
                                                const formattedDate = docInfo.dateUploaded ? 
                                                    formatDate(docInfo.dateUploaded) : 'N/A';
                                                setDocumentToDelete({ 
                                                    id: documentId, 
                                                    filename,
                                                    documentType: docInfo.documentType || category.label,
                                                    dateUploaded: formattedDate,
                                                    deliveryId: docInfo.deliveryId || deliveryId
                                                });
                                                setShowDeleteConfirmation(true);
                                            }}
                                            getDocumentInfo={getDocumentInfo}
                                            formatDate={formatDate}
                                            formatFileSize={formatFileSize}
                                            getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                                        />
                                    ))}

                                    {uncategorizedDocs.length > 0 && (
                                        <DeliveryDocumentGroup
                                            documentType="Other"
                                            categoryColor="#6b7280"
                                            documents={uncategorizedDocs}
                                            onDownload={downloadDocument}
                                            onPreview={previewDocument}
                                            onDelete={(documentId, filename) => {
                                                const fullDocument = documents.find(doc => doc.Id === documentId);
                                                const docInfo = fullDocument ? getDocumentInfo(fullDocument) : {};
                                                const formattedDate = docInfo.dateUploaded ? 
                                                    formatDate(docInfo.dateUploaded) : 'N/A';
                                                setDocumentToDelete({ 
                                                    id: documentId, 
                                                    filename,
                                                    documentType: docInfo.documentType || 'Other',
                                                    dateUploaded: formattedDate,
                                                    deliveryId: docInfo.deliveryId || deliveryId
                                                });
                                                setShowDeleteConfirmation(true);
                                            }}
                                            getDocumentInfo={getDocumentInfo}
                                            formatDate={formatDate}
                                            formatFileSize={formatFileSize}
                                            getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                                        />
                                    )}

                                    {searchQuery && filteredCategoryGroups.every(c => c.documents.length === 0) && uncategorizedDocs.length === 0 && (
                                        <div className="doc-panel-empty-state" style={{ padding: '2rem' }}>
                                            <p className="doc-panel-empty-text">No documents match "{searchQuery}"</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
        </div>
    );

    // Docked mode is placed by the host screen's grid and gets its heading and
    // close button from the pane around it, so it renders neither the
    // full-screen overlay nor a second header of its own.
    return (
        <>
            {docked ? (
                <div className="document-panel document-panel--docked" ref={panelRef}>
                    {panelBody}
                </div>
            ) : (
                <div className="document-panel-overlay">
                    <div className="document-panel" ref={panelRef}>
                        <div className="document-panel-header">
                            <h2>Delivery Documents</h2>
                            <button
                                className="close-button"
                                onClick={onClose}
                                aria-label="Close panel"
                            >
                                <CloseIconSmall />
                            </button>
                        </div>
                        {panelBody}
                    </div>
                </div>
            )}

            {/* Read the paperwork instead of downloading it. Inside the split
                pane the pane itself renders the document beside the delivery,
                so this pop-up is only for hosts that have no pane. */}
            <PdfPreviewModal
                isOpen={Boolean(previewPdfUrl) && !viewer}
                pdfUrl={previewPdfUrl}
                fileName={previewFileName}
                onClose={closePreview}
            />

            {/* Upload Modal */}
            <DeliveryDocumentUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                categories={effectiveCategories}
                onDocumentSelect={handleFileUpload}
                uploading={uploading}
                deliveryId={deliveryId}
                partnerName={effectivePartnerName}
            />

            {/* Duplicate Detection Modal -- sent to the top of the page like
                the upload pop-up, or the docked column would keep it under
                the delivery's date boxes. */}
            {showDuplicateModal && duplicateFileInfo && createPortal(
                <div className="confirmation-popup-overlay">
                    <div className="confirmation-popup">
                        <h3 style={{ 
                            margin: '0 0 1rem 0', 
                            color: '#374151',
                            fontSize: '1.25rem',
                            fontWeight: '600'
                        }}>
                            Duplicate File Detected
                        </h3>
                        <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                            A file with the name <strong>"{duplicateFileInfo.file.name}"</strong> already exists in this delivery's documents.
                        </p>
                        <p style={{ margin: '0 0 1.5rem 0', color: '#374151' }}>
                            What would you like to do?
                        </p>
                        <div style={{ 
                            display: 'inline-flex', 
                            gap: '0.5rem', 
                            justifyContent: 'flex-end' 
                        }}>
                            <button
                                type="button"
                                onClick={handleDuplicateKeepBoth}
                                disabled={uploading}
                                style={{
                                    background: uploading ? '#6c757d' : 'linear-gradient(135deg, #10b981, #059669)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.75rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    opacity: uploading ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                            >
                                Keep Both
                            </button>
                            <button
                                type="button"
                                onClick={handleDuplicateReplace}
                                disabled={uploading}
                                style={{
                                    background: uploading ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.75rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    opacity: uploading ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                            >
                                Replace
                            </button>
                            <button
                                type="button"
                                onClick={handleDuplicateCancel}
                                style={{
                                    background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.75rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && documentToDelete && (
                <TradeOpsDeleteModal
                    isOpen={showDeleteConfirmation}
                    onClose={() => {
                        setShowDeleteConfirmation(false);
                        setDocumentToDelete(null);
                    }}
                    onConfirm={() => {
                        deleteDocument(documentToDelete.id, documentToDelete.filename);
                        setShowDeleteConfirmation(false);
                        setDocumentToDelete(null);
                    }}
                    itemType="document"
                    itemDetails={{
                        filename: documentToDelete.filename,
                        documentType: documentToDelete.documentType,
                        dateUploaded: documentToDelete.dateUploaded,
                        deliveryId: documentToDelete.deliveryId
                    }}
                />
            )}
        </>
    );
};

export default DocumentPanel; 